<template>
  <div id="map" ref="mapElement">
    <button id="homeButton" @click="goHome()">
      <i class="vs-icon notranslate icon-scale vs-button--icon material-icons null"
        style="order: 0; margin-right: 0px; margin-left: 0px">
        home
      </i>
    </button>
    <slot v-if="ready" />
  </div>
</template>
<script>
import "leaflet/dist/leaflet.css";
import "leaflet-draw/dist/leaflet.draw.css";
import L from "leaflet";
import "leaflet-draw/dist/leaflet.draw.js";
import "proj4leaflet/lib/proj4-compressed";
import "proj4leaflet/src/proj4leaflet";
/* Fix for default-icon leaflet bug */
import "leaflet-defaulticon-compatibility/dist/leaflet-defaulticon-compatibility.webpack.css";
import "leaflet-defaulticon-compatibility";
/* Grouped layers plugin */
import "leaflet-groupedlayercontrol/src/leaflet.groupedlayercontrol.css";
import "leaflet-groupedlayercontrol/src/leaflet.groupedlayercontrol";

import "leaflet-side-by-side";

import providers from "@/config/leaflet-providers";
export default {
  props: {
    baseLayers: {
      type: Array,
      required: false,
      default() {
        return [
          {
            name: "esri-gray",
            alias: "Gray - ESRI"
          }
        ];
      }
    },
    center: {
      type: Object,
      required: false,
      default: function() {
        return { lon: -2.9, lat: 43.853, zoom: 3 };
      }
    },
    marker: {
      type: Object,
      required: false,
      default: function() {
        return {
          lon: 0,
          lat: 0
        };
      }
    },
    controlLayers: {
      type: Boolean, // on/off
      default: false
    },
    layerGroups: {
      type: Array,
      required: false,
      default() {
        return [];
      }
    }
  },
  data() {
    return {
      baseMapLayer: {},
      drawnItems: {},
      initialView: this.center,
      groupedLayersControl: {},
      sideBySideLayersControl: {},
      markerLayer: {},
      mapObj: {}
    };
  },
  computed: {
    ready() {
      return Object.keys(this.mapObj).length > 0;
    }
  },
  watch: {
    center: function(newView) {
      this.centerMap(newView);
    },
    marker: function(newPoint) {
      this.updateMarker(newPoint);
    }
  },
  mounted() {
    this.initMap();
  },
  methods: {
    initMap() {
      this.mapObj = L.map(this.$refs["mapElement"], {
        timeDimension: true,
        timeDimensionControl: true,
        timeDimensionControlOptions: {
          autoPlay: false,
          playButton: true,
          loopButton: false,
          speedSlider: false,
          displayDate: true,
          playerOptions: {
            buffer: 10,
            transitionTime: 1000,
            loop: true
          }
        }
      });
      this.addDrawControls();
      if (this.controlLayers) {
        this.addLayersControl();
        this.addBaseLayers();
      }
      this.setMapEvents();
      this.centerMap(this.initialView);
    },
    centerMap(view) {
      this.mapObj.setView([view.lat, view.lon], view.zoom);
    },
    addDrawControls() {
      /// todo: sacar los draw controls a un componente
      L.drawLocal.draw.toolbar.buttons.marker =
        "Set a position marker on the map";
      this.drawnItems = L.featureGroup().addTo(this.mapObj);
      this.mapObj.addControl(
        new L.Control.Draw({
          position: "topleft",
          edit: {
            featureGroup: this.drawnItems,
            edit: false,
            remove: false
          },
          draw: {
            polyline: false,
            polygon: false,
            rectangle: false,
            circle: false,
            circlemarker: false,
            marker: true
          }
        })
      );
    },
    addBaseLayers() {
      for (let i = 0; i < this.baseLayers.length; i++) {
        const provider = this.getProvider(this.baseLayers[i].name);
        const baseMapLayer = L.tileLayer(provider.url, provider.props);
        this.groupedLayersControl.addBaseLayer(
          baseMapLayer,
          this.baseLayers[i].alias
        );
        if (i === 0) {
          // Activate first by default
          baseMapLayer.addTo(this.mapObj);
        }
      }
    },
    getProvider: name => {
      const provider = providers.find(provider => {
        return provider.name === name;
      });
      return provider || providers[0];
    },
    addLayersControl() {
      this.groupedLayersControl = L.control.groupedLayers();
      this.groupedLayersControl.options.autoZIndex = false;
      this.groupedLayersControl.addTo(this.mapObj);
      // this.setExclusiveLayerSelection();
    },
    setExclusiveLayerSelection() {
      for (let i = 0; i < this.layerGroups.length; i++) {
        this.groupedLayersControl.options.exclusiveGroups.push(
          this.layerGroups[i]
        );
      }
    },
    setMapEvents() {
      const self = this;
      this.mapObj.on("draw:created", function(e) {
        self.drawnItems.clearLayers();
        const type = e.layerType;
        self.markerLayer = e.layer;

        if (type === "marker") {
          const geoJson = self.markerLayer.toGeoJSON();
          const lon = geoJson.geometry.coordinates[0].toFixed(6);
          const lat = geoJson.geometry.coordinates[1].toFixed(6);
          self.$emit("marker-drawn", { lon: lon, lat: lat });
        }
        self.drawnItems.addLayer(self.markerLayer, true);
      });
    },
    updateMarker(newPoint) {
      const newLatLng = new L.LatLng(newPoint.lat, newPoint.lon);
      this.drawnItems.clearLayers();
      this.markerLayer = L.marker(newLatLng);
      this.drawnItems.addLayer(this.markerLayer, true);
      this.centerMap({ lat: newPoint.lat, lon: newPoint.lon, zoom: 7 });
    },
    goHome() {
      this.$emit("go-home");
    }
  }
};
</script>

<style scoped>
#map {
  height: 100%;
  width: 100%;
}
#homeButton {
  position: absolute;
  top: 125px;
  left: 11px;
  z-index: 500;
  border: 1px solid rgba(0, 160, 158, 0.5);
  background-color: white;
  width: 32px;
  height: 32px;
  line-height: 30px;
  border-radius: 2px;
  cursor: pointer;
}
#homeButton:hover {
  background-color: #F4F4F4;
}
</style>
